'use client';

import { Svg, Text } from '@/components';
import { useLocation } from '@/context/locationContext';
import { autocomplete } from '@/features/Location/components/google';
import { CheckIcon, LocationPinIcon } from '@/icons';
import LocationIcon from '@/icons/LocationIcon';
import { debounce } from '@/utils/helper';
import { storage } from '@/utils/storage';
import { PlaceAutocompleteResult } from '@googlemaps/google-maps-services-js';
import { forwardRef, useCallback, useState, useEffect, useMemo } from 'react';
import { getGooglePlaceDetails, getGoogleReverseGeocode } from '@/app/actions';
import { twMerge } from 'tailwind-merge';
import { logger } from '@/config';
import { AddressUserDetails } from '@/models/user-details';
import StoreChangeModal from '../components/StoreChangeModal';
import { DEFAULT_STOCK_ID, DEFAULT_STORE_CODE } from '@/constants';
import { useCleverTap } from '@/context/clevertapContext';

interface AddressFieldProps {
  onServiceabilityChange?: (isServiceable: boolean) => void;
  onAddressSelect?: (data: {
    address: string;
    city?: string;
    state?: string;
    latitude?: string;
    longitude?: string;
  }) => void;
  ref?: any;
  placeholder?: string;
  className?: string;
  defaultValue?: string;
  isAddressForm?: boolean;
  isCartSection?: boolean;
  setSelected?: React.Dispatch<React.SetStateAction<AddressUserDetails | null>>;
  onPredictionsVisibilityChange?: (isVisible: boolean) => void;
  onSearchComplete?: () => void;
  isDeliveryDetails?: boolean;
  onOptionSelect?: () => void;
  onDeliveryDetailModalClose?: () => void;
  isCheckout?: boolean;
  isCustomerAddressForm?: boolean;
}

const AddressField = forwardRef<HTMLInputElement, AddressFieldProps>(
  (
    {
      onServiceabilityChange,
      onAddressSelect,
      placeholder = 'Search a new address',
      className,
      defaultValue = '',
      isAddressForm = false,
      isCartSection = false,
      setSelected,
      onPredictionsVisibilityChange,
      onSearchComplete,
      isDeliveryDetails = false,
      onOptionSelect,
      onDeliveryDetailModalClose,
      isCheckout = false,
      isCustomerAddressForm = false,
    },
    ref
  ) => {
    const { pushEvent } = useCleverTap();
    const [predictions, setPredictions] = useState<PlaceAutocompleteResult[]>(
      []
    );
    // Add state for active search
    const [input, setInput] = useState('');
    const [isEditing, setIsEditing] = useState(false);
    const [isServiceable, setIsServiceable] = useState<boolean | null>(null);
    const [selectedAddress, setSelectedAddress] = useState('');
    const [address, setAddress] = useState('');
    const [showStoreChangeModal, setShowStoreChangeModal] = useState(false);
    const {
      setEmptyCart,
      setAddress: setLocationAddress,
      pendingAddressChange,
      setPendingAddressChange,
      setSelectedPrediction,
      setAddressString,
      setHeaderLocationTitle,
      geolocationOptions,
      setIsDelivery,
    } = useLocation();
    const [isProcessingChange, setIsProcessingChange] = useState(false);
    const [isSelectingAddress, setIsSelectingAddress] = useState(false);
    // Add state for fetching location
    const [isFetchingLocation, setIsFetchingLocation] = useState(false);
    // Add state for location permission
    const [locationPermission, setLocationPermission] =
      useState<PermissionState | null>(null);
    const [locationError, setLocationError] = useState<string | null>(null);
    const [isAddressChanged, setIsAddressChanged] = useState(false);

    const fetchPredictions = async (inputValue: string) => {
      const predictions = await autocomplete(inputValue);
      setPredictions(predictions ?? []);
    };
    const { addressString } = useLocation();
    useEffect(() => {
      const addressStored = isCustomerAddressForm
        ? storage.get('customer_addressString')
        : storage.get('addressString');
      if (addressStored) {
        setAddress(addressStored);
      }
    }, [addressString]);

    const debouncedLocationSearch = useCallback(
      debounce(fetchPredictions, 500),
      []
    );
    const { handleSuggestionClick, getMagentoStoreData, emptyCart } =
      useLocation();

    const handleSuggestionSelect = async (prediction: any) => {
      try {
        setSelectedPrediction(prediction);
        // Only store prediction if it's valid
        if (prediction && prediction.place_id) {
          storage.set('prediction', JSON.stringify(prediction));
        }
        setIsSelectingAddress(true);
        setSelected?.(null);
        storage.set('selectedAddressId', '');
        setLocationError(null);
        const googlePlaceDetails = await getGooglePlaceDetails(
          prediction.place_id
        );

        if (!googlePlaceDetails) {
          throw new Error('Failed to get place details');
        }

        let data;
        try {
          const parsedData = JSON.parse(googlePlaceDetails);
          data = parsedData.data;
        } catch (error) {
          logger.error('Error parsing place details:', error);
          throw new Error('Invalid place details response');
        }

        setIsDelivery(true);
        // For cart section, handle store change differently
        if (isCartSection && data.store_code) {
          const currentStoreCode = storage.get('store_code');

          if (currentStoreCode && data.store_code !== currentStoreCode) {
            setPendingAddressChange({ prediction, data, isAddressField: true });
            setShowStoreChangeModal(true);
            setIsSelectingAddress(false);
            return;
          }
        }

        await handleSuggestionClick({
          prediction: prediction,
          address: selectedAddress,
          skipToast: isDeliveryDetails ? false : true,
          isDeliveryPopup: true,
          isCustomerAddressForm: isCustomerAddressForm,
        });

        // For non-cart sections or same store, proceed normally
        await processAddressChange(prediction, data);

        setIsSelectingAddress(false);
      } catch (error) {
        logger.error('Error in handleSuggestionSelect:', error);
        setIsSelectingAddress(false);
      }
    };

    const processAddressChange = async (
      prediction: any,
      data: any,
      lat?: number,
      lng?: number
    ) => {
      try {
        const selectedAddress = prediction.description;

        // Update all address states
        setInput(selectedAddress);
        setSelectedAddress(selectedAddress);
        setAddress(selectedAddress);
        setLocationAddress(selectedAddress);
        setAddressString(selectedAddress);
        // Store address and related data
        if (!isCustomerAddressForm) {
          storage.set('addressString', selectedAddress);

          if (data?.store_code && prediction.place_id) {
            storage.set('address', prediction.structured_formatting?.main_text);
            setHeaderLocationTitle(prediction.structured_formatting?.main_text);
            storage.set(
              'addressTitle',
              prediction.structured_formatting?.main_text
            );
            storage.set(
              'addressDescription',
              prediction.structured_formatting?.secondary_text
            );
          }

          // Store the components
          if (data?.store_code) {
            storage.set(
              'store_code',
              data?.store_code || data?.default_store_code || DEFAULT_STORE_CODE
            );
            storage.set('stock_id', data?.stock_id || DEFAULT_STOCK_ID);
            storage.set('home_store_code', data?.store_code);
            storage.set('wizzy_api_key', data?.wizzy_api_key);
            storage.set('wizzy_store_id', data?.wizzy_store_id);
          }
          if (
            data?.result?.[0]?.lat &&
            data?.result?.[0]?.lng &&
            data?.store_code
          ) {
            storage.set('lat', data?.result?.[0]?.lat);
            storage.set('lng', data?.result?.[0]?.lng);
          }
          if (lat && lng && data?.store_code) {
            storage.set('lat', lat.toString());
            storage.set('lng', lng.toString());
          }

          let cityPart = '';
          let statePart = '';

          // Extract city and state from address components

          // Fallback to parsing from address string if components not found

          if (
            data?.result?.[0]?.city &&
            data?.result?.[0]?.state &&
            data?.store_code
          ) {
            cityPart = data?.result?.[0]?.city;
            statePart = data?.result?.[0]?.state;
          } else {
            if (!cityPart || !statePart) {
              const addressParts = selectedAddress
                .split(',')
                .map((part: string) => part.trim());

              // Try to find city and state in address parts
              cityPart = addressParts[addressParts.length - 3];
              statePart = addressParts[addressParts.length - 2];
            }
          }
          if (data?.store_code) {
            storage.set('city', cityPart);
            storage.set('state', statePart);
          }
          if (onAddressSelect) {
            onAddressSelect({
              address: selectedAddress,
              city: cityPart || '',
              state: statePart || '',
              latitude: storage.get('lat') || '',
              longitude: storage.get('lng') || '',
            });
          }
          // Handle callbacks
        } else {
          storage.set('customer_addressString', selectedAddress);

          if (
            data?.result?.[0]?.lat &&
            data?.result?.[0]?.lng &&
            data?.store_code
          ) {
            storage.set('customer_lat', data?.result?.[0]?.lat);
            storage.set('customer_lng', data?.result?.[0]?.lng);
          }
          if (lat && lng && data?.store_code) {
            storage.set('customer_lat', lat.toString());
            storage.set('customer_lng', lng.toString());
          }

          let cityPart = '';
          let statePart = '';

          // Extract city and state from address components

          // Fallback to parsing from address string if components not found

          if (
            data?.result?.[0]?.city &&
            data?.result?.[0]?.state &&
            data?.store_code
          ) {
            cityPart = data?.result?.[0]?.city;
            statePart = data?.result?.[0]?.state;
          } else {
            if (!cityPart || !statePart) {
              const addressParts = selectedAddress
                .split(',')
                .map((part: string) => part.trim());

              // Try to find city and state in address parts
              cityPart = addressParts[addressParts.length - 3];
              statePart = addressParts[addressParts.length - 2];
            }
          }
          if (data?.store_code) {
            storage.set('customer_city', cityPart);
            storage.set('customer_state', statePart);
          }
          if (onAddressSelect) {
            onAddressSelect({
              address: selectedAddress,
              city: cityPart || '',
              state: statePart || '',
              latitude: storage.get('customer_lat') || '',
              longitude: storage.get('customer_lng') || '',
            });
          }
        }

        // Handle completion callbacks
        onSearchComplete?.();
        onOptionSelect?.();
        onPredictionsVisibilityChange?.(false);
        onDeliveryDetailModalClose?.();
      } catch (error) {
        logger.error('Error in processAddressChange:', error);
        throw error;
      }
    };

    // Update the useEffect to properly handle the emptyCart state
    useEffect(() => {
      const handlePendingChange = async () => {
        if (emptyCart && pendingAddressChange && !isProcessingChange) {
          try {
            setIsProcessingChange(true);

            // Process address change immediately
            await processAddressChange(
              pendingAddressChange.prediction,
              pendingAddressChange.data
            );
            // Reset states
            setShowStoreChangeModal(false);
            setPendingAddressChange(null);
            setIsProcessingChange(false);

            // Only set emptyCart to false if not in checkout
            if (!isCheckout) {
              setEmptyCart(false);
              window.location.reload();
            }
          } catch (error) {
            logger.error('Error handling pending change:', error);
            setEmptyCart(false);
            setPendingAddressChange(null);
            setIsProcessingChange(false);
            setShowStoreChangeModal(false);
          }
        }
      };

      handlePendingChange();
    }, [emptyCart, pendingAddressChange, isProcessingChange, isCheckout]);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      setIsEditing(true);
      setInput(value);
      setSelectedAddress(value);
      setAddress(value);
      setIsAddressChanged(true);
      // Allow empty input
      if (!value || !value.trim()) {
        setPredictions([]);
        setSelectedAddress('');
        setAddress('');
        return;
      }

      // Only search if input has 3 or more characters
      if (value.trim().length >= 3) {
        debouncedLocationSearch(value);
      } else {
        setPredictions([]);
      }
    };

    const handleInputBlur = () => {
      setTimeout(() => {
        const storedAddress = isCustomerAddressForm
          ? storage.get('customer_addressString')
          : storage.get('addressString');
        if ((!input || !isEditing) && storedAddress) {
          setInput(storedAddress);
          setSelectedAddress(storedAddress);
          setAddress(storedAddress);
        }
        setIsEditing(false);
      }, 200);
    };

    // const handleInputFocus = () => {
    //   setIsEditing(true);
    //   // Select all text when focusing
    //   const inputElement = document.getElementById(
    //     'searchBar'
    //   ) as HTMLInputElement;
    //   if (inputElement) {
    //     inputElement.select();
    //   }
    // };

    const handleGetCurrentLocation = async () => {
      try {
        setSelected?.(null);
        storage.set('selectedAddressId', '');
        setInput('Fetching your location...');
        setIsFetchingLocation(true);
        setIsEditing(false);

        if (!navigator.geolocation) {
          logger.error('Geolocation is not supported by your browser.');
          setIsFetchingLocation(false);
          return;
        }

        // If permission is denied, prompt user to enable location
        if (locationPermission === 'denied') {
          setInput('');
          setIsFetchingLocation(false);
          // Show instructions to enable location
          setLocationError(
            'Location access is blocked. Please enable location access in your browser settings and try again.'
          );
          return;
        }

        const handleSuccess = useCallback(
          (
            resolve: (arg0: any) => void,
            pos: { coords: { latitude: any; longitude: any; accuracy: any } }
          ) => {
            pushEvent({
              event: 'GPS Location Permission Query',
              data: {
                'Page URL': window.location.href,
                'Page Name': document.title,
                Permission: 'Granted',
                Position: `${pos.coords.latitude}, ${pos.coords.longitude}`,
                Accuracy: pos.coords?.accuracy,
                'Prompt Source': 'Address Text Field',
              },
            });
            setLocationPermission('granted');
            resolve(pos);
          },
          [setLocationPermission, pushEvent]
        );

        const handleError = useCallback(
          (reject: (arg0: any) => void, error: { code: number }) => {
            if (error.code === 1) {
              // Update permission state if user denies
              pushEvent({
                event: 'GPS Location Permission Query',
                data: {
                  'Page URL': window.location.href,
                  'Page Name': document.title,
                  Permission: 'Denied',
                  'Prompt Source': 'Address Text Field',
                },
              });
              setLocationPermission('denied');
            }
            reject(error);
          },
          [setLocationPermission, pushEvent]
        );

        const position = await new Promise<GeolocationPosition>(
          (resolve, reject) => {
            navigator.geolocation.getCurrentPosition(
              // Granted Scenario
              position => handleSuccess(resolve, position),
              // Denied Scenario
              error => handleError(reject, error),
              geolocationOptions
            );
          }
        );

        const { latitude, longitude } = position.coords;

        // Get address from coordinates first
        const geocodeDataString = await getGoogleReverseGeocode(
          latitude,
          longitude,
          1
        );
        if (geocodeDataString) {
          const { data } = JSON.parse(geocodeDataString);
          pushEvent({
            event: 'GPS Location Fetched',
            data: {
              'Page URL': window.location.href,
              'Page Name': document.title,
              'Store Code':
                data?.store_code ||
                data?.default_store_code ||
                DEFAULT_STORE_CODE,
              Serviceable: data?.store_code && data?.store_code !== '',
              'Prompt Source': 'Address Text Field',
            },
          });
          setIsDelivery(true);
          // Check for store code change
          if (isCartSection && data.store_code) {
            const currentStoreCode = storage.get('store_code');

            if (currentStoreCode && data.store_code !== currentStoreCode) {
              setIsFetchingLocation(false);
              setPendingAddressChange({
                lat: latitude,
                lng: longitude,
                prediction: {
                  description:
                    data.result?.[0]?.title + ',' + data.result?.[0]?.sub_title,
                },
                data,
                isAddressField: true,
              });

              setShowStoreChangeModal(true);
              return;
            }
          }

          await getMagentoStoreData({
            coords: {
              latitude,
              longitude,
              accuracy: position.coords.accuracy,
              skipToast: isDeliveryDetails ? false : true,
              intentionalClick: 1,
              isAddressField: true,
              isDeliveryPopup: true,
              isCustomerAddressForm: isCustomerAddressForm,
            },
          });

          if (data?.result?.[0]) {
            const addressString =
              data.result[0].title + ',' + data.result[0].sub_title;
            await processAddressChange(
              {
                place_id: data.result[0].place_id,
                description: addressString,
              },
              data,
              latitude,
              longitude
            );
          }
        }
        setIsFetchingLocation(false);
      } catch (error: any) {
        logger.error('Error getting current location:', error);
        setInput('');
        let errorMessage = 'Unable to retrieve location.';
        if (error.code === 1) {
          errorMessage =
            'Please allow location access and try again or search location manually.';
        }
        if (error.code === 2) errorMessage = 'Position unavailable.';
        if (error.code === 3) errorMessage = 'Location request timed out.';
        setLocationError(errorMessage);
        logger.error(errorMessage);
        setIsFetchingLocation(false);
      }
    };

    // Check initial serviceability
    useEffect(() => {
      if (defaultValue) {
        // If there's a default value, use stored serviceability
        const serviceable = isCustomerAddressForm
          ? storage.get('customer_serviceable')
          : storage.get('serviceable');
        const isServiceable = serviceable === 'true';
        setIsServiceable(isServiceable);
        onServiceabilityChange?.(isServiceable);
      }
    }, [defaultValue, onServiceabilityChange]);

    // Add effect to sync input with address changes
    useEffect(() => {
      if (!isEditing) {
        const currentAddress = selectedAddress || addressString || '';
        setInput(currentAddress);
      }
    }, [selectedAddress, addressString, isEditing]);

    // Initialize current address
    useEffect(() => {
      const storedAddress = isCustomerAddressForm
        ? storage.get('customer_addressString')
        : storage.get('addressString');
      if (storedAddress) {
        setInput(storedAddress);
      }
    }, []);

    // Update input when defaultValue changes
    useEffect(() => {
      if (!isEditing) {
        setInput(defaultValue);
      }
    }, [defaultValue, isEditing]);

    // Initialize input value based on context
    useEffect(() => {
      if (isAddressForm) {
        if (defaultValue) {
          // Edit mode - show saved address
          setInput(defaultValue);
          setAddress(defaultValue);
        } else {
          // Add mode - keep empty
          setInput('');
          setAddress('');
        }
      } else {
        // Non-form context (Cart, etc.)
        const currentAddress = isCustomerAddressForm
          ? storage.get('customer_addressString')
          : storage.get('addressString');
        if (currentAddress) {
          setInput(currentAddress);
          setAddress(currentAddress);
        }
      }
    }, [isAddressForm, defaultValue]);

    // Don't show current location for address form
    useEffect(() => {
      const addressStored = isCustomerAddressForm
        ? storage.get('customer_addressString')
        : storage.get('addressString');
      if (addressStored) {
        setAddress(addressStored);
      }
    }, [addressString]);

    useEffect(() => {
      if (defaultValue) {
        setInput(defaultValue);
      }
    }, [defaultValue]);

    // Simplify displayValue logic
    const displayValue = useMemo(() => {
      if (isSelectingAddress) return 'Selecting the address...';
      if (isFetchingLocation) return 'Fetching your location...';
      return input;
    }, [isSelectingAddress, isFetchingLocation, input]);

    // Remove effects that might interfere with current location
    useEffect(() => {
      if (!isEditing && !isAddressChanged) {
        const storedAddress = isCustomerAddressForm
          ? storage.get('customer_addressString')
          : storage.get('addressString');
        if (storedAddress) {
          setInput(storedAddress);
          setSelectedAddress(storedAddress);
          setAddress(storedAddress);

          // Also check serviceability
          const serviceable = isCustomerAddressForm
            ? storage.get('customer_serviceable')
            : storage.get('serviceable');
          setIsServiceable(serviceable === 'true');
          onServiceabilityChange?.(serviceable === 'true');
        }
      }
    }, [isEditing, onServiceabilityChange]);

    // Add effect to notify parent of predictions visibility
    useEffect(() => {
      if (isDeliveryDetails) {
        const isPredictionsVisible = Boolean(
          isEditing && input.trim() && predictions.length > 0
        );
        onPredictionsVisibilityChange?.(isPredictionsVisible);
      }
    }, [
      isEditing,
      input,
      predictions.length,
      isDeliveryDetails,
      onPredictionsVisibilityChange,
    ]);

    // Check location permission on mount
    useEffect(() => {
      const checkLocationPermission = async () => {
        try {
          const permission = await navigator.permissions.query({
            name: 'geolocation',
          });
          setLocationPermission(permission.state);

          // Listen for permission changes
          permission.addEventListener('change', () => {
            setLocationPermission(permission.state);
          });
        } catch (error) {
          logger.error('Error checking location permission:', error);
        }
      };

      checkLocationPermission();
    }, []);

    return (
      <>
        <div className='relative w-full'>
          <div className='relative flex items-center'>
            <input
              ref={ref}
              id='searchBar'
              type='search'
              spellCheck='false'
              role='search'
              autoComplete='off'
              aria-label='search-bar'
              placeholder={
                isSelectingAddress
                  ? 'Selecting the address...'
                  : isFetchingLocation
                    ? 'Fetching your location...' // Show fetching text in placeholder
                    : placeholder
              }
              value={displayValue} // Use empty string when fetching
              onChange={handleInputChange}
              // onFocus={handleInputFocus}
              onBlur={handleInputBlur}
              autoFocus={false}
              disabled={isSelectingAddress || isFetchingLocation} // Disable input while fetching
              className={twMerge(
                'search-input w-full rounded-lg bg-black/10 px-[0.8rem] py-3 pr-14 outline-hidden backdrop-blur-xs',
                'placeholder:select-none placeholder:truncate placeholder:text-base placeholder:text-[#38383A] placeholder:opacity-70',
                'lg:py-3',
                (isFetchingLocation || isSelectingAddress) && 'text-gray-400',
                className
              )}
            />
            <div
              className={twMerge(
                'pointer-events-auto absolute right-0 z-10 flex h-full w-12 items-center justify-center',
                locationPermission === 'denied' && 'opacity-50' // Visual indication of disabled state
              )}
              onClick={handleGetCurrentLocation}>
              <button
                type='button'
                disabled={isSelectingAddress}
                aria-label='Get current location'
                title={
                  locationPermission === 'denied'
                    ? 'Location access is blocked. Please enable in browser settings.'
                    : 'Get current location'
                }
                className='flex h-8 w-8 items-center justify-center hover:opacity-80'>
                <Svg className='pointer-events-none h-4 w-4 stroke-fig stroke-[1.5]'>
                  <LocationIcon />
                </Svg>
              </button>
            </div>
          </div>

          {isEditing && input.trim() && predictions.length > 0 && (
            <div
              className={twMerge(
                'absolute left-0 right-0 top-full z-10 mt-1 rounded-lg bg-[#EDEAE9] shadow-lg',
                isDeliveryDetails && 'max-h-[250px] overflow-y-auto'
              )}>
              <ul className='py-2'>
                {predictions.map((prediction, index) => (
                  <li
                    key={prediction.place_id || index}
                    onClick={() => handleSuggestionSelect(prediction)}
                    className='cursor-pointer px-4 py-2 text-left hover:bg-gray-100'>
                    <div className='flex items-start gap-3'>
                      <div className='shrink-0 pt-1'>
                        <Svg className='h-6 w-6 stroke-[#D48A7A] stroke-[1.5]'>
                          <LocationPinIcon />
                        </Svg>
                      </div>
                      <div className='flex flex-col'>
                        <span className='font-medium text-[#38383A]'>
                          {prediction.structured_formatting?.main_text}
                        </span>
                        <span className='text-sm text-gray-500'>
                          {prediction.structured_formatting?.secondary_text}
                        </span>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {!isSelectingAddress &&
            !isFetchingLocation &&
            !isEditing &&
            isServiceable !== null &&
            address && (
              <div className='mt-2 text-sm'>
                {isServiceable &&
                storage.get('store_change_fig') === 'false' ? (
                  <div className='flex items-center gap-x-1'>
                    <Svg className='rounded-full bg-fig stroke-white p-1'>
                      <CheckIcon />
                    </Svg>
                    <Text className='p-0 text-xs text-fig px-0'>
                      We can deliver to your location
                    </Text>
                  </div>
                ) : !isServiceable ? (
                  <div className='flex items-center gap-2'>
                    <Text className='text-xs text-fig px-0'>
                      Sorry, we don't deliver here yet. Try searching different
                      address.
                    </Text>
                  </div>
                ) : null}
              </div>
            )}
        </div>
        <StoreChangeModal
          show={showStoreChangeModal}
          onClose={() => {
            setShowStoreChangeModal(false);
            setPendingAddressChange(null);
            setEmptyCart(false);
          }}
          pendingAddressChange={pendingAddressChange}
        />
        {locationError && !isEditing && (
          <div className='mt-2 px-2 text-xs text-fig'>{locationError}</div>
        )}
      </>
    );
  }
);

AddressField.displayName = 'AddressField';
export default AddressField;
